import React, { useEffect, useMemo, useRef, useState } from 'react'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { Action, Context } from 'types/GlobalUserEvents'
import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'
import { FilterSegmentParams } from './ChartView/ChartElement'
import { Filters, Sort } from 'utils/types'
import { SidebarDialogProps } from 'components_new/molecules/SidebarDialog'
import { TDataDialogEventContext, TDataDialogSource } from './types'
import { TMetricQueryParams, TMetricSourceRef } from 'types/GlobalMetric'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'

import { buildMetricView, visibleEntityColumns } from 'utils/metricView'

import Button from 'components_new/atoms/Button'

import SidebarDialog from 'components_new/molecules/SidebarDialog'

import ChartView from './ChartView'
import LeftSidebar from './LeftSidebar'
import RightSidebar from './RightSidebar'
import TableView from './TableView'
import Toolbar from './Toolbar'
import Title from './Title'

import { useMetricData } from './useMetricData'
import { getActiveMetricView } from './sourceView'
import { getChatSource, hasChatHistory } from './chatSource'

export interface DataDialogProps {
  source: TDataDialogSource | null
  // Baseline filters/period resolved by the caller (segment overrides applied
  // inside the dialog). See TDataDialogSource for the source contract.
  baseFilters: TMetricQueryParams['filter']
  basePeriod: CustomPeriodFilter | PeriodFilter | null
  depth?: SidebarDialogProps['depth']
  eventContext: TDataDialogEventContext
  onClose: () => void
  segmentFilters: Filters
  segmentLabel: string | number | null
  segmentPeriod: CustomPeriodFilter | null
}

const DataDialog = (props: DataDialogProps) => {
  const {
    source,
    baseFilters,
    basePeriod,
    depth,
    eventContext,
    onClose,
    segmentFilters,
    segmentLabel,
    segmentPeriod
  } = props

  // The dialog stays mounted while closed so it can animate out; "open" is
  // simply whether there is a source to show.
  const open = source !== null

  const { allowAiChat } = useAccountSettings()
  const { t } = useTranslation()

  const [isChatting, setIsChatting] = useState<boolean>(false)
  const [view, setView] = useState<'chart' | 'table'>('table')

  const {
    activeView,
    columns,
    exportAllRows,
    fetched,
    filterLabels,
    hasMore,
    localFilters,
    mergedFilters,
    onAddLocalFilter,
    onChangeView,
    onDeleteLocalFilter,
    onEditColumns,
    onSearch,
    onSort,
    parsedData,
    periodFields,
    rawData,
    reset,
    sortedOnServer,
    status
  } = useMetricData({
    source,
    baseFilters,
    basePeriod,
    segmentFilters,
    segmentPeriod,
    open
  })

  const entityId = activeView?.entityId ?? null
  // Several views = the left sidebar shows a switcher between them.
  const views = source && source.views.length > 1 ? source.views : undefined
  // Chat attribution is derived from where the dialog was opened.
  const chatSource = getChatSource(eventContext.context)
  const chatSourceId = activeView?.chatSourceId

  // The metric identity the chat anchors to, or null when chat is unavailable.
  const activeMetricView = useMemo(
    () => getActiveMetricView(activeView),
    [activeView]
  )
  const chatDisabled = !activeMetricView

  const metricView = useMemo(() => {
    if (!activeMetricView) return undefined

    return buildMetricView({
      ...activeMetricView,
      filters: mergedFilters,
      period: periodFields,
      entityColumns: visibleEntityColumns(columns)
    })
  }, [activeMetricView, mergedFilters, periodFields, columns])

  // Charts for kpi option views fetch via the stored widget so the widget's
  // and option's saved filter conditions apply.
  // Metric and key metric views have no stored conditions and
  // fetch as plain metrics.
  const chartMetricSource: TMetricSourceRef | undefined = useMemo(
    () =>
      activeView?.sourceType === 'kpi_option'
        ? { type: 'kpi_option', id: activeView.id }
        : undefined,
    [activeView]
  )

  // context defaults to the entry point on eventContext; input is the
  // per-event detail (toggle target, sorted column, filtered attribute, ...).
  const logEvent = (
    action: Action,
    input?: string | null,
    context: Context = eventContext.context
  ) => {
    UserEvents.log({
      action,
      context,
      input: input ?? null,
      dashboard_id: eventContext.dashboardId ?? null,
      dashboard_group_id: eventContext.dashboardGroupId ?? null,
      analysis_id: eventContext.analysisId ?? null,
      report_id: eventContext.reportId ?? null
    })
  }

  // Columns already logged as searched during this open. A search is a text
  // field, so clearing it to try another term is the same intent, not a new
  // one worth its own event.
  const searchedColumns = useRef<Set<string>>(new Set())

  // Log the open once per open transition, from inside the dialog so every
  // entry point is covered uniformly. input carries the explored source type.
  const wasOpen = useRef(false)

  useEffect(() => {
    if (open && !wasOpen.current) {
      logEvent(Action.SHOW_POSTS, activeView?.sourceType)
    }

    wasOpen.current = open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleClose = () => {
    reset()
    setView('table')
    setIsChatting(false)
    searchedColumns.current.clear()

    onClose()
  }

  const handleChangeChartView = (value: 'chart' | 'table') => {
    setView(value)

    logEvent(Action.SHOW_POSTS_VIEW_CHANGED, value)
  }

  const handleSwitchView = (id: string | null) => {
    onChangeView(id)

    logEvent(Action.SHOW_POSTS_VIEW_SWITCHED)
  }

  const handleSort = (sort: Sort) => {
    onSort(sort)

    logEvent(Action.SHOW_POSTS_SORTED)
  }

  // At most one event per column and open, never per keystroke.
  const handleSearch = (columnId: string, query: string) => {
    onSearch(columnId, query)

    if (query.length > 0 && !searchedColumns.current.has(columnId)) {
      searchedColumns.current.add(columnId)

      logEvent(Action.SHOW_POSTS_COLUMN_SEARCHED, columnId)
    }
  }

  const handleAddLocalFilter = (value: FilterSegmentParams) => {
    onAddLocalFilter(value)

    logEvent(Action.SHOW_POSTS_FILTERED)
  }

  const handleDeleteLocalFilter = (params: { id: string; value: string }) => {
    onDeleteLocalFilter(params)

    logEvent(Action.SHOW_POSTS_FILTER_REMOVED)
  }

  const handleExported = () => {
    logEvent(Action.SHOW_POSTS_EXPORTED)
  }

  const handleEditColumns = (value: Parameters<typeof onEditColumns>[0]) => {
    onEditColumns(value)

    logEvent(Action.COLUMNS_IN_SHOW_POSTS_EDITED)
  }

  const handleLinkClick = () => {
    logEvent(Action.NAVIGATED_TO_SOURCE, null, Context.FROM_SHOW_POSTS)
  }

  return (
    <SidebarDialog
      actions={
        <Button onClick={handleClose} variant="text">
          {t('global', 'close')}
        </Button>
      }
      depth={depth}
      noGutter={view === 'table'}
      fullHeight
      fullWidth
      leftSidebar={{
        content: (
          <LeftSidebar
            activeViewId={activeView?.id}
            filterLabels={filterLabels}
            fromDate={rawData?.from_date}
            localFilters={localFilters}
            metricView={metricView}
            onChangeView={handleSwitchView}
            onDeleteLocalFilter={handleDeleteLocalFilter}
            toDate={rawData?.to_date}
            views={views}
          />
        ),
        size: 'small'
      }}
      maxWidth={false}
      onClose={handleClose}
      open={open}
      rightSidebar={
        chatDisabled || !allowAiChat
          ? undefined
          : {
              content: (
                <RightSidebar
                  hasMoreRows={hasMore}
                  historyEnabled={hasChatHistory(chatSource)}
                  onExpandedChange={setIsChatting}
                  onLogEvent={logEvent}
                  metricView={metricView}
                  rowCount={
                    status === 'loading' || status === 'failed'
                      ? null
                      : parsedData.rows.length
                  }
                  source={chatSource}
                  sourceId={chatSourceId}
                />
              ),
              size: isChatting ? 'large' : 'medium'
            }
      }
      title={
        <Title
          live={eventContext.context === Context.FROM_INSIGHT}
          segment={segmentLabel}
        />
      }
      toolbar={
        <Toolbar
          columns={columns}
          entityId={entityId}
          fetched={fetched}
          hasMore={hasMore}
          metricView={metricView}
          onExportAll={exportAllRows}
          onChangeView={handleChangeChartView}
          onEditColumns={handleEditColumns}
          onExported={handleExported}
          parsedData={parsedData}
          rawData={rawData}
          view={view}
        />
      }
    >
      {view === 'chart' ? (
        <ChartView
          metricView={metricView}
          onFilter={handleAddLocalFilter}
          source={chartMetricSource}
        />
      ) : (
        <TableView
          hasMore={hasMore}
          onLinkClick={handleLinkClick}
          onSearch={handleSearch}
          onSort={handleSort}
          parsedData={parsedData}
          sortedOnServer={sortedOnServer}
          status={status}
        />
      )}
    </SidebarDialog>
  )
}

export default DataDialog
